import { CoreAttr } from '@infa-hawk/models';

import { ElasticSearchHit, IdentityType, IElasticSearchResp, IGetAssetsArgs, IGetObjects, IGetRelationshipsArgs } from './ElasticService.types';
import { HybridSearchApi, SearchXINFASEARCHLANGUAGE } from './generated/apis/HybridSearchApi';

export class ElasticService {
	hybridSearchApi;
	tenantId;

	constructor(hybridSearchApi: HybridSearchApi, tenantId: string) {
		this.hybridSearchApi = hybridSearchApi;
		this.tenantId = tenantId;
	}

	async execute(query: any): Promise<IElasticSearchResp> {
		const resp = await this.hybridSearchApi.search({
			xINFAORGID: this.tenantId,
			xINFASEARCHLANGUAGE: SearchXINFASEARCHLANGUAGE.Elasticsearch,
			body: query,
		});

		return {
			hits: resp?.hits?.hits?.map((hit: any) => hit.sourceAsMap) || [],
			totalHits: resp?.hits?.totalHits || 0,
			aggregations: resp?.aggregations || {},
		};
	}

	async getObjects(args: IGetObjects) {
		const { from = 0, size = 10, must = {}, mustNot = {}, should = {}, filters = {}, postFilters = {}, fields, sort = {}, aggs, find } = args;

		const query = {
			from,
			size,
			query: {
				bool: {
					must: [
						...Object.entries(must)
							.filter((x) => x[1]?.length)
							.map(([key, value]) => ({
								terms: {
									[key]: value,
								},
							})),
						this.getFindQuerySyntax(find),
					].filter(Boolean),
					must_not: Object.entries(mustNot).length
						? Object.entries(mustNot)
								.filter((x) => x[1]?.length)
								.map(([key, value]) => ({
									terms: {
										[key]: value,
									},
								}))
						: undefined,
					filter: Object.entries(filters).length
						? Object.entries(filters)
								.filter((x) => x[1]?.length)
								.map(([key, value]) => ({
									terms: {
										[key]: value,
									},
								}))
						: undefined,
					...(Object.entries(should).length
						? {
								should: Object.entries(should)
									.filter((x) => x[1]?.length)
									.map(([key, value]) => ({
										terms: {
											[key]: value,
										},
									})),
								minimum_should_match: 1,
							}
						: {}),
				},
			},
			post_filter: {
				bool: {
					filter: Object.entries(postFilters)
						.filter((x) => x[1]?.length)
						.map(([key, value]) => ({
							terms: {
								[key]: value,
							},
						})),
				},
			},
			fields,
			sort:
				Object.entries(sort).length > 0
					? Object.entries(sort).map(([key, order]) => ({
							[key]: { order },
						}))
					: undefined,
			aggs: aggs?.length
				? aggs.reduce(
						(x, item) => ({
							...x,
							[item]: {
								terms: {
									field: item,
									size: 10000,
								},
							},
						}),
						{},
					)
				: undefined,
		};

		return this.execute(query);
	}

	async getAssetCollections(assetId: string, supplementInformation: Record<string, string>[]) {
		const query = {
			query: {
				bool: {
					must: [
						{
							term: {
								elementType: 'COLLECTION',
							},
						},
					],
					should: supplementInformation.map(({ type, supplementId }) => ({
						bool: {
							must: [
								{
									term: {
										type,
									},
								},
								{
									term: {
										[supplementId]: assetId,
									},
								},
							],
						},
					})),
					minimum_should_match: 1,
				},
			},
		};
		return this.execute(query);
	}

	async getRelatedAssets(relationshipInfo) {
		if (!relationshipInfo.length) {
			return {};
		}

		const relationships = await this.execute({
			from: 0,
			size: 10000,
			query: {
				bool: {
					must: [
						{
							term: {
								elementType: 'RELATIONSHIP',
							},
						},
					],
					should: relationshipInfo
						.map(({ direction, ids, associationIds, associationKinds }) => ({
							bool: {
								must: [
									{
										terms: {
											[direction === 'OUT' ? CoreAttr.SourceIdentity : CoreAttr.TargetIdentity]: ids,
										},
									},
									associationIds?.length && {
										terms: {
											type: associationIds,
										},
									},
									associationKinds?.length && {
										terms: {
											'core.associationKind': associationKinds,
										},
									},
								].filter(Boolean),
							},
						}))
						.filter(Boolean),
					minimum_should_match: 1,
				},
			},
		}).then((x) => x.hits);

		const assetIds = relationshipInfo
			.map(({ direction, ids, associationIds, associationKinds }) =>
				relationships
					?.filter(
						(x) =>
							(direction !== 'IN' || ids.includes(x[CoreAttr.TargetIdentity])) &&
							(direction !== 'OUT' || ids.includes(x[CoreAttr.SourceIdentity])) &&
							(associationIds?.find((y) => x.type.includes(y)) || associationKinds?.includes(x['core.associationKind'])),
					)
					?.map((x) => x?.[direction === 'IN' ? CoreAttr.SourceIdentity : CoreAttr.TargetIdentity]),
			)
			.flat()
			.filter(Boolean);

		const assets = await this.getAssetsByIds(assetIds, IdentityType.Internal);

		return { assets, relationships };
	}

	async getAssets(args: IGetAssetsArgs) {
		const { ids, identityType = IdentityType.Internal, fields, sort, offset, size, find, aggs, filters = {}, postFilters = {}, mustNot = {} } = args;

		return this.getObjects({
			from: offset,
			size: 'size' in args ? size : ids?.length,
			must: {
				elementType: ['OBJECT'],
				[CoreAttr.Identity]: identityType === IdentityType.Internal ? ids : undefined,
				[CoreAttr.ExternalId]: identityType === IdentityType.External ? ids : undefined,
			},
			mustNot,
			filters,
			postFilters,
			find: {
				text: find,
				fields: [CoreAttr.Name],
			},
			fields,
			sort,
			aggs,
		});
	}

	async getRelationships(args: IGetRelationshipsArgs) {
		const {
			offset = 0,
			size = 10,
			associationIds = [],
			associationKinds = [],
			sourceIdentities = [],
			targetIdentities = [],
			filters = {},
			postFilters = {},
			mustNot = {},
			aggs = [],
		} = args;

		return this.getObjects({
			from: offset,
			size,
			must: {
				'elementType': ['RELATIONSHIP'],
				'type': associationIds,
				'core.associationKind': associationKinds,
				[CoreAttr.SourceIdentity]: sourceIdentities,
				[CoreAttr.TargetIdentity]: targetIdentities,
			},
			mustNot,
			filters,
			postFilters,
			aggs,
		});
	}

	async getAssetById(id: string, identityType: IdentityType = IdentityType.Internal) {
		if (!id) {
			return;
		}
		const resp = await this.getAssets({ ids: [id], identityType });
		return resp.hits[0];
	}

	async getAssetsByIds(ids: string[], identityType: IdentityType = IdentityType.Internal) {
		if (!ids || ids.length === 0) {
			return [];
		}
		const resp = await this.getAssets({ ids, identityType });
		return resp.hits;
	}

	async getAssetsByLocations(locations: string[]): Promise<ElasticSearchHit[]> {
		const { hits } = await this.getAssets({
			size: locations.length,
			filters: {
				'core.location::path_hierarchy.exact': locations,
			},
		});

		const locs = hits.map((x) => x[CoreAttr.Location]);
		const unique = new Set(locs);

		if (locs.length > unique.size) {
			// this means that we got assets with duplicate locations.
			// this also means that the result set might have ignored the location which was requested, so make another call with missed locations.
			// btw. this is just a hack, ideally there shouldn't be 2 assets with same location.
			const missed = locations.filter((x) => !locs.find((l) => l === x));

			const missedHits = await this.getAssetsByLocations(missed);
			return [...hits, ...missedHits];
		}

		return hits;
	}

	getFindQuerySyntax(find: { text: string; fields: string[] }, type = 'bool_prefix', isExactMatch = true) {
		const { text, fields } = find || {};

		return (
			text && {
				multi_match: {
					query: text,
					fields: [...fields, ...fields.map((attribute) => `${attribute}::text.reverse_lowercase`)],
					type,
					operator: isExactMatch ? 'and' : 'or',
				},
			}
		);
	}
}
